'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SignupShell } from '../components/SignupShell';
import type { LegalDocumentId } from './legal-document-paths';
import type { LegalDocument } from './legal-document-schema';
import { loadLegalDocument } from './load-legal-document';
import { LegalDocumentView } from './LegalDocumentView';
import { ensureLegalDocumentStyle } from './legal-style';

/**
 * The heading a URL hash names, e.g. `#cookie-notice`, or null. Only a
 * heading's own anchor counts; a malformed hash names nothing.
 */
function hashTarget(legalDocument: LegalDocument): HTMLElement | null {
  let id: string;
  try {
    id = decodeURIComponent(window.location.hash.slice(1));
  } catch {
    return null;
  }
  const isHeadingAnchor = legalDocument.blocks.some(
    (block) => block.type === 'heading' && block.anchorId === id,
  );
  return id && isHeadingAnchor ? document.getElementById(id) : null;
}

function LegalDocumentPage({ documentId }: { documentId: LegalDocumentId }) {
  ensureLegalDocumentStyle();
  const { t } = useTranslation('signup');
  const titleSuffix = t('documentTitleSuffix');
  const [loaded, setLoaded] = useState<LegalDocument | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let active = true;
    loadLegalDocument(documentId).then(
      (document) => {
        if (active) setLoaded(document);
      },
      (error: unknown) => {
        if (active) setFailure(error);
      },
    );
    return () => {
      active = false;
    };
  }, [documentId]);

  useEffect(() => {
    if (loaded) document.title = `${loaded.title} | ${titleSuffix}`;
  }, [loaded, titleSuffix]);

  // Once the text is on the page: land on the section a hash names, as the
  // legacy page did, otherwise on the document's heading (ADR-025). This runs
  // on arrival only, so a language switch never moves the reader.
  useEffect(() => {
    if (!loaded) return;
    const target = hashTarget(loaded);
    if (target) target.scrollIntoView();
    else headingRef.current?.focus();
  }, [loaded]);

  // A chunk that fails to load is the app error boundary's to recover from.
  if (failure) throw failure;

  return (
    <SignupShell width="reading" languageSwitch={false}>
      {loaded ? <LegalDocumentView document={loaded} headingRef={headingRef} /> : null}
    </SignupShell>
  );
}

/**
 * A fixed legal document as its own page inside the signup chrome. It is a
 * page, not a step: it reads and writes nothing in the flow store. The site
 * footer links these documents from every page. The documents exist in
 * English only, so the header draws no language switch.
 */
export function LegalDocumentScreen({ documentId }: { documentId: LegalDocumentId }) {
  return <LegalDocumentPage documentId={documentId} />;
}
